import { TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps, useState } from 'react';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { useResultsStoryAssets } from '@sorare/core/src/components/animations/AnimatedStory/useResultsStoryAssets';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { LastFixtureStoryStep } from 'components/so5/LastFixtureStory/LastFixtureStoryStep';

import { DELAY_BEFORE_ANIMATION_MS } from '../CardLayout';
import { Title } from '../LastFixtureStoryStep/Title';
import { StoryState } from '../types';
import { LevelUp } from './LevelUp';
import type {
  CardLeveledUp_cardXpGradeThreshold,
  CardLeveledUp_so5Fixture,
  CardLeveledup_leveledUpCard,
} from './__generated__/index.graphql';

const Wrapper = styled(Horizontal).attrs({ center: true })`
  @media ${tabletAndAbove} {
    padding: var(--triple-unit) 0;
  }
`;

type ContentProps = {
  leveledUpCards: ComponentProps<typeof CardLeveledUp>['leveledUpCards'];
  gradeThresholds?: ComponentProps<typeof CardLeveledUp>['gradeThresholds'];
};
const Content = ({ leveledUpCards, gradeThresholds }: ContentProps) => {
  const isTabletAndAbove = useIsTabletAndAbove();

  const [alreadyfocusedCard, setAlreadyFocusedCard] = useState<number[]>();

  const itemToDisplayOnDesktop = Math.min(3, leveledUpCards.length);
  const itemToDisplay = isTabletAndAbove ? itemToDisplayOnDesktop : 1;

  return (
    <div
      style={{
        width: `calc(${itemToDisplay} * 250px + var(--double-unit) * ${itemToDisplay - 1})`,
        maxHeight: '70vh',
      }}
    >
      <Scrollable
        itemToDisplay={itemToDisplay}
        withMask
        onVisibleItemsChanged={items =>
          setAlreadyFocusedCard(prev => [...(prev || []), ...items])
        }
        visibleVerticalOverflowSize={200}
      >
        {leveledUpCards.map((leveledUpCard, i) => (
          <Wrapper key={leveledUpCard.id}>
            <LevelUp
              cardLeveledUp={leveledUpCard}
              gradeThresholds={gradeThresholds}
              focused={alreadyfocusedCard?.includes(i)}
              delay={i < itemToDisplay ? DELAY_BEFORE_ANIMATION_MS : 0}
            />
          </Wrapper>
        ))}
      </Scrollable>
    </div>
  );
};

type Props = {
  from?: StoryState;
  so5Fixture?: CardLeveledUp_so5Fixture;
  leveledUpCards: CardLeveledup_leveledUpCard[];
  gradeThresholds?: CardLeveledUp_cardXpGradeThreshold[];
  onNext?: ComponentProps<typeof LastFixtureStoryStep>['onNext'];
  onClose: ComponentProps<typeof LastFixtureStoryStep>['onClose'];
};
export const CardLeveledUp = ({
  from,
  so5Fixture,
  leveledUpCards,
  gradeThresholds,
  onNext,
  onClose,
}: Props) => {
  const { intro, tunnel } = useResultsStoryAssets();

  return (
    <LastFixtureStoryStep
      title={<Title type="cardLeveledUp" so5Fixture={so5Fixture} />}
      video={{
        asset: from === 'intro' ? intro : tunnel,
        color: '#26B0CE',
        colorDelay: from === 'intro' ? 800 : 0,
      }}
      onNext={onNext}
      onClose={onClose}
      noScroll
    >
      <Content
        leveledUpCards={leveledUpCards}
        gradeThresholds={gradeThresholds}
      />
    </LastFixtureStoryStep>
  );
};

CardLeveledUp.fragments = {
  cardXpGradeThreshold: gql`
    fragment CardLeveledUp_cardXpGradeThreshold on CardXPGradeThreshold {
      ...LevelUp_cardXpGradeThreshold
    }
    ${LevelUp.fragments.cardXpGradeThreshold}
  ` as TypedDocumentNode<CardLeveledUp_cardXpGradeThreshold>,
  so5Fixture: gql`
    fragment CardLeveledUp_so5Fixture on So5Fixture {
      slug
      ...Title_so5Fixture
    }
    ${Title.fragments.so5Fixture}
  ` as TypedDocumentNode<CardLeveledUp_so5Fixture>,
  leveledUpCard: gql`
    fragment CardLeveledup_leveledUpCard on LeveledUpCard {
      id
      ...LevelUp_leveledUpCard
    }
    ${LevelUp.fragments.leveledUpCard}
  ` as TypedDocumentNode<CardLeveledup_leveledUpCard>,
};
